import { Asset } from '../../../models/image/dto/asset';
import { BulkEditAssetsResponse } from '../../../models/image/responses/bulk-edit-assets-response';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';
import { applyUpdatedAssets } from './asset-patch';
import { applyUpdatedFolders } from './folder-patch';
import { findFolderInLibrary } from './asset-library-lookups';
import { MediaManagerFileSystem } from 'src/app/models/media-manager/media-manager-file-system';

/**
 * Reconcile a full bulk-edit response by folding the updated destination folder into the update sets and then
 * running the normal per-item update patches. Returns false if any single lookup or patch fails so the caller
 * can fall back to a full refetch.
 */
export function applyBulkEditResponse(
  response: BulkEditAssetsResponse,
  currentFolders: MediaManagerFolderDto[],
  currentAssets: Asset[],
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const currentFoldersById = new Map<string, MediaManagerFolderDto>();
  currentFolders.forEach(currentFolder => {
    currentFoldersById.set(currentFolder.id, currentFolder);
  });
  // Ensure the destination folder has a canonical current entry because the bulk response
  // includes server-updated metadata for it after items are moved into that folder.
  const responseTargetFolder = response.updatedTargetFolder?.folder;
  if (responseTargetFolder) {
    const currentTargetFolder = findFolderInLibrary(responseTargetFolder.id, assetLibrary);
    if (!currentTargetFolder) {
      return false;
    }
    currentFoldersById.set(currentTargetFolder.id, currentTargetFolder);
  }
  const currentAssetsById = new Map<string, Asset>();
  currentAssets.forEach(currentAsset => {
    currentAssetsById.set(currentAsset.getUniqueIdentifier(), currentAsset);
  });
  const updatedAssetFolders = response.updatedAssetFolders ?? [];
  const updatedAssets = response.updatedAssets ?? [];
  const updatedFoldersById = new Map<string, MediaManagerFolderDto>();
  updatedAssetFolders.forEach(assetFolder => {
    updatedFoldersById.set(assetFolder.id, assetFolder);
  });
  // The bulk move response includes the destination folder separately so we can refresh
  // its server-updated metadata (for example lastModified) after items are moved into it.
  if (responseTargetFolder) {
    updatedFoldersById.set(responseTargetFolder.id, responseTargetFolder);
  }
  if (!applyUpdatedFolders(currentFoldersById, Array.from(updatedFoldersById.values()), filesystem)) {
    return false;
  }
  if (!applyUpdatedAssets(currentAssetsById, updatedAssets, filesystem)) {
    return false;
  }
  return true;
}
